import { API_BASE, request } from './api-client';
import type { AdminUserEntitlementView, PublishTransactionRow } from '../types';

const base = (userId: string) => `${API_BASE}/admin/users-mgmt/${userId}`;

export function getEntitlement(userId: string) {
  return request<AdminUserEntitlementView>(`${base(userId)}/entitlements`);
}

export function listPublishHistory(userId: string) {
  return request<PublishTransactionRow[]>(`${base(userId)}/publish-history`);
}

/**
 * `idempotencyKey` is sent as the `Idempotency-Key` header, never in the body
 * (the backend rejects unknown body fields). The same key → the grant lands
 * once, so a double-click or a retried request cannot grant twice.
 */
export function grantCredits(
  userId: string,
  { idempotencyKey, ...input }: { amount: number; reason?: string; idempotencyKey?: string },
) {
  return request<{ publishCredits: number }>(`${base(userId)}/credits/grant`, {
    method: 'POST',
    body: JSON.stringify(input),
    ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
  });
}

export function revokeCredits(userId: string, input: { amount: number; reason?: string }) {
  return request<{ publishCredits: number }>(`${base(userId)}/credits/revoke`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ── Rupee wallet (VALUE credits, 1 credit = ₹1) — support corrections ────────
export interface WalletView {
  balance: number;
  blendedDiscountPercent: number | null;
  /** FI-01 / H3: open holds — non-empty means the wallet cannot be spent (bank dispute open, or lost and under review). */
  frozen?: Array<{ id: string; reason: string; disputeId: string | null; since: string }>;
}

export function getWallet(userId: string) {
  return request<WalletView>(`${base(userId)}/wallet`);
}

/** `idempotencyKey` goes in the header (the backend rejects unknown body fields): a double-click grants once. */
export function grantWallet(userId: string, { idempotencyKey, ...input }: { amount: number; reason: string; idempotencyKey?: string }) {
  return request<{ walletBalance: number }>(`${base(userId)}/wallet/grant`, {
    method: 'POST',
    body: JSON.stringify(input),
    ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
  });
}

/** FI-01 / H3: release a wallet held for a bank dispute, once support has reviewed it (audited). */
export function unfreezeWallet(userId: string, input: { reason: string }) {
  return request<{ released: number }>(`${base(userId)}/wallet/unfreeze`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function revokeWallet(userId: string, input: { amount: number; reason: string }) {
  return request<{ walletBalance: number }>(`${base(userId)}/wallet/revoke`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
